/**
 * ANNOUNCE-002 — global announcements banner.
 * Shows unread active announcements (all-staff for everyone; IT-only extras
 * for IT staff) on every authenticated page, with per-user Mark as Read.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Megaphone, Check } from 'lucide-react';
import { formatDateOnly } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_STYLES = {
  critical: { border: '#dc2626', bg: '#fef2f2', badge: '#dc2626' },
  important: { border: '#d97706', bg: '#fffbeb', badge: '#d97706' },
  normal: { border: '#2563eb', bg: '#eff6ff', badge: '#2563eb' },
};

export const AnnouncementsBanner = () => {
  const [items, setItems] = useState([]);
  const [marking, setMarking] = useState(null);
  const lastFetchRef = useRef(0);
  const location = useLocation();

  const fetchFeed = useCallback(async (force = false) => {
    const token = localStorage.getItem('token');
    if (!token) { setItems([]); return; }
    const now = Date.now();
    if (!force && now - lastFetchRef.current < 60000) return;
    lastFetchRef.current = now;
    try {
      const res = await fetch(`${API}/api/it-dashboard/announcements/feed`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      // SHIFT-ANNOUNCE-001: requires_ack items are handled by the mandatory ShiftChangeAcknowledgeModal
      setItems((data.announcements || []).filter((a) => !a.is_read && !a.requires_ack));
    } catch { /* silent — banner is best-effort */ }
  }, []);

  useEffect(() => { fetchFeed(); }, [location.pathname, fetchFeed]);
  useEffect(() => {
    const t = setInterval(() => fetchFeed(true), 300000);
    return () => clearInterval(t);
  }, [fetchFeed]);

  const markRead = async (id) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setMarking(id);
    try {
      await fetch(`${API}/api/it-dashboard/announcements/${id}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems((prev) => prev.filter((a) => a.id !== id));
    } catch { /* keep item; user can retry */ }
    setMarking(null);
  };

  if (!items.length) return null;

  return (
    <div
      data-testid="announcements-banner"
      style={{
        position: 'fixed', top: 10, left: '50%', transform: 'translateX(-50%)',
        zIndex: 1900, width: 'min(720px, calc(100vw - 32px))',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      {items.slice(0, 3).map((a) => {
        const s = PRIORITY_STYLES[a.priority] || PRIORITY_STYLES.normal;
        return (
          <div
            key={a.id}
            data-testid={`announcement-banner-item-${a.id}`}
            style={{
              background: s.bg, border: `1px solid ${s.border}`, borderLeft: `4px solid ${s.border}`,
              borderRadius: 10, padding: '10px 14px', boxShadow: '0 6px 20px rgba(15,23,42,0.12)',
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}
          >
            <Megaphone size={18} style={{ color: s.badge, flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 14, color: '#0f172a' }}>{a.title}</strong>
                <span style={{
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                  color: '#fff', background: s.badge, borderRadius: 999, padding: '2px 8px',
                }}>
                  {a.priority}
                </span>
              </div>
              <div style={{ fontSize: 13, color: '#334155', marginTop: 3, whiteSpace: 'pre-wrap' }}>
                {a.content}
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                IT Announcement · {a.created_by_name}
                {a.expires_at ? ` · expires ${formatDateOnly(a.expires_at)}` : ''}
              </div>
            </div>
            <button
              data-testid={`announcement-mark-read-${a.id}`}
              onClick={() => markRead(a.id)}
              disabled={marking === a.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
                border: `1px solid ${s.border}`, background: '#fff', color: s.badge,
                borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <Check size={13} /> Mark as Read
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default AnnouncementsBanner;
